import React, { useMemo, useState } from "react";
import { Download, FileSpreadsheet } from "lucide-react";
import { TspCard, TspTable, TspBreadcrumb, TspEmptyState, TspBadge } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import notification from "../../../../helpers/notification";
import useTspReportsData from "./useTspReportsData";
import downloadReport from "./downloadReport";
import { REPORT_TYPES, EXPORT_FORMATS } from "./reportTypes";
import { tspErrorMessage } from "../../hooks/useTspApiResource";

const NO_FILTERS = { from: "", to: "" };

// TSP Reports (Phase 5): pick a report, filter by date range, see it as a
// table with totals, export it. The table's columns come straight from the
// server's response (report.columns), so a new report never needs a new
// frontend column mapping — see reports/service.js on the backend.
export default function TspReportsPage() {
  const [reportKey, setReportKey] = useState(REPORT_TYPES[0].key);
  const [draftFilters, setDraftFilters] = useState(NO_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(NO_FILTERS);
  const [exporting, setExporting] = useState(null); // the format currently downloading, or null

  const filters = useMemo(() => {
    const clean = {};
    if (appliedFilters.from) clean.from = appliedFilters.from;
    if (appliedFilters.to) clean.to = appliedFilters.to;
    return clean;
  }, [appliedFilters]);

  const { data, loading, error, reload } = useTspReportsData(reportKey, filters);

  const selectReport = (key) => {
    setReportKey(key);
    setDraftFilters(NO_FILTERS);
    setAppliedFilters(NO_FILTERS);
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters(draftFilters);
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      await downloadReport(reportKey, format, filters);
    } catch (exportError) {
      notification("error", tspErrorMessage(exportError, "Could not export this report."));
    } finally {
      setExporting(null);
    }
  };

  const renderBody = () => {
    if (error) return <TspLoadError error={error} onRetry={reload} what="this report" />;
    if (loading || !data) return <div className="tsp-page tsp-dashboard-loading">Loading report…</div>;
    if (data.rows.length === 0) return <TspEmptyState icon={FileSpreadsheet} title="No data for this report yet" message="Try a different date range, or check back once more activity is recorded." />;
    return <TspTable columns={data.columns} rows={data.rows.map((row, index) => ({ ...row, __key: index }))} rowKey="__key" className="tsp-table-boxed" />;
  };

  return (
    <div className="tsp-page tsp-reports-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Reports</h1>
          <TspBreadcrumb items={["Dashboard", "Reports"]} />
        </div>
      </div>

      <TspCard
        title="Select Report"
        action={
          <div className="tsp-card-action-group">
            {EXPORT_FORMATS.map((format) => (
              <button
                key={format.key}
                type="button"
                className="tsp-btn tsp-btn-outline tsp-btn-sm"
                disabled={!data || data.rows.length === 0 || exporting !== null}
                onClick={() => handleExport(format.key)}
              >
                <Download size={13} /> {exporting === format.key ? "Exporting…" : format.label}
              </button>
            ))}
          </div>
        }
      >
        <div className="tsp-journey-form-grid">
          <label className="tsp-field">
            <span className="tsp-field-label">Report Type</span>
            <select className="tsp-field-control" value={reportKey} onChange={(event) => selectReport(event.target.value)}>
              {REPORT_TYPES.map((type) => (
                <option key={type.key} value={type.key}>
                  {type.label}
                </option>
              ))}
            </select>
          </label>

          <form className="tsp-report-date-filters" onSubmit={applyFilters}>
            <label className="tsp-field">
              <span className="tsp-field-label">From</span>
              <input type="date" className="tsp-field-control" value={draftFilters.from} onChange={(event) => setDraftFilters((prev) => ({ ...prev, from: event.target.value }))} />
            </label>
            <label className="tsp-field">
              <span className="tsp-field-label">To</span>
              <input type="date" className="tsp-field-control" value={draftFilters.to} onChange={(event) => setDraftFilters((prev) => ({ ...prev, to: event.target.value }))} />
            </label>
            <button type="submit" className="tsp-btn tsp-btn-primary">
              Apply Filters
            </button>
          </form>
        </div>
      </TspCard>

      <TspCard
        title={REPORT_TYPES.find((type) => type.key === reportKey)?.label}
        action={
          data?.totals && (
            <span className="tsp-card-action-group">
              {Object.entries(data.totals).map(([key, value]) => (
                <TspBadge key={key} tone="tag">
                  {key.replace(/_/g, " ")}: {value ?? "—"}
                </TspBadge>
              ))}
            </span>
          )
        }
      >
        {renderBody()}
      </TspCard>

      <ToastContainer />
    </div>
  );
}
